import {
    ExportFormat,
    Feature,
    hasAnyRequiredPermissions,
    LEDGER_PERMISSIONS,
    Role,
    toDateString,
} from "@pentacode/core";
import {
    canAccess,
    defineRoutes,
    routeKeys,
    routePath,
    type MenuSection,
    type RouteAccess,
    type RouteNode,
    type RouteParamArgs,
    type RoutePath,
} from "../lib/route-tree";

const today = () => ({ date: toDateString(new Date()) });

const employeeRecords: MenuSection = {};
const employeeProfile: MenuSection = {};

const allLedgers = ({ hasPermission }: RouteAccess) => LEDGER_PERMISSIONS.every(hasPermission);

const forEmployee = ({ employee }: RouteAccess) => !!employee?.accessible;
const forHiredEmployee = (access: RouteAccess) => forEmployee(access) && !access.employee?.probation;

const notEmbedded = ({ embedded }: RouteAccess) => !embedded;
const austriaOnly = ({ company }: RouteAccess) => company?.country === "AT";

const payroll = { label: "Lohnbuchhaltung", icon: "icon.object.labour-cost" } as const;
const workingTime = { label: "Arbeitszeiten", icon: "icon.object.time-history" } as const;
const roster = { label: "Dienstplan", icon: "icon.feature.roster" } as const;
const balances = { label: "Konten", icon: "icon.object.time-balance" } as const;
const masterData = { label: "Stammdaten", icon: "icon.object.master-data" } as const;
const cashbook = { label: "Kassenbuch", icon: "icon.object.cashbook" } as const;
const custom = { label: "Individuelle Exporte", icon: "icon.object.rules" } as const;

const payrollExport = (
    label: string,
    icon: "icon.file.pdf" | "icon.file.payroll" | "icon.file.csv" | "icon.file.excel"
) => ({ label, icon, permission: "manage.exports.payroll", menuSection: payroll }) as const;

export const appRoutes = defineRoutes({
    dashboard: { label: "Dashboard", icon: "icon.feature.dashboard", available: notEmbedded },
    time: { label: "Zeiterfassung", icon: "icon.feature.time-tracking", permission: "manage.time", linkParams: today },
    roster: { label: "Dienstplan", icon: "icon.feature.roster", permission: "manage.roster", linkParams: today },
    employees: {
        label: "Mitarbeiter",
        icon: "icon.feature.employees",
        permission: "manage.employees",
        children: {
            all: {
                label: "Alle Mitarbeiter",
                children: {
                    time: {
                        label: "Arbeitszeiten",
                        icon: "icon.object.working-hours",
                        permission: "manage.employees.time",
                        menuSection: employeeRecords,
                    },
                    "time-balance": {
                        label: "Arbeitszeitkonto",
                        icon: "icon.object.time-balance",
                        permission: "manage.employees.ledgers_time",
                        menuSection: employeeRecords,
                    },
                    "vacation-balance": {
                        label: "Urlaubskonto",
                        icon: "icon.time-entry.vacation",
                        permission: "manage.employees.ledgers_vacation",
                        menuSection: employeeRecords,
                    },
                    "bonuses-balance": {
                        label: "Zuschlagskonto",
                        icon: "icon.object.bonus",
                        permission: "manage.employees.ledgers_bonuses",
                        menuSection: employeeRecords,
                    },
                    absences: {
                        label: "Abwesenheiten",
                        icon: "icon.object.absence",
                        permission: "manage.employees.absences",
                        menuSection: employeeRecords,
                    },
                    availabilities: {
                        label: "Verfügbarkeiten",
                        icon: "icon.planning.availability",
                        permission: "manage.employees.availabilities",
                        menuSection: employeeRecords,
                    },
                    documents: {
                        label: "Digitale Personalakte",
                        icon: "icon.file.collection",
                        permission: "manage.employees.documents",
                        menuSection: employeeRecords,
                    },
                    advances: {
                        label: "Vorschüsse",
                        icon: "icon.object.labour-cost",
                        permission: "manage.employees.advances",
                        menuSection: employeeRecords,
                    },
                    "ledgers-month": {
                        label: "Konten (Übersicht)",
                        icon: "icon.object.time-balance",
                        permitted: allLedgers,
                        menuSection: employeeRecords,
                    },
                    "ledgers-year": { label: "Konten (Übersicht)", permitted: allLedgers, menuHidden: true },
                    "core-data": {
                        label: "Stammdaten",
                        icon: "icon.object.master-data",
                        permission: "manage.employees.core_data",
                        menuSection: employeeProfile,
                    },
                    positions: {
                        label: "Positionen",
                        icon: "icon.object.position",
                        permission: "manage.employees.positions",
                        menuSection: employeeProfile,
                    },
                    permissions: {
                        label: "Zugänge & Rechte",
                        icon: "icon.object.permissions",
                        permission: "manage.employees.permissions",
                        menuSection: employeeProfile,
                    },
                    contracts: {
                        label: "Verträge",
                        icon: "icon.object.contract",
                        permission: "manage.employees.contract",
                        menuSection: employeeProfile,
                    },
                },
            },
            ":employeeId": {
                label: "Mitarbeiter",
                children: {
                    time: {
                        label: "Arbeitszeiten",
                        icon: "icon.object.working-hours",
                        permission: "manage.employees.time",
                        permitted: forHiredEmployee,
                        menuSection: employeeRecords,
                    },
                    "time-balance": {
                        label: "Arbeitszeitkonto",
                        icon: "icon.object.time-balance",
                        permission: "manage.employees.ledgers_time",
                        permitted: forHiredEmployee,
                        menuSection: employeeRecords,
                    },
                    "vacation-balance": {
                        label: "Urlaubskonto",
                        icon: "icon.time-entry.vacation",
                        permission: "manage.employees.ledgers_vacation",
                        permitted: forHiredEmployee,
                        menuSection: employeeRecords,
                    },
                    "bonuses-balance": {
                        label: "Zuschlagskonto",
                        icon: "icon.object.bonus",
                        permission: "manage.employees.ledgers_bonuses",
                        permitted: forHiredEmployee,
                        menuSection: employeeRecords,
                    },
                    absences: {
                        label: "Abwesenheiten",
                        icon: "icon.object.absence",
                        permission: "manage.employees.absences",
                        permitted: forHiredEmployee,
                        menuSection: employeeRecords,
                    },
                    availabilities: {
                        label: "Verfügbarkeiten",
                        icon: "icon.planning.availability",
                        permission: "manage.employees.availabilities",
                        permitted: forHiredEmployee,
                        menuSection: employeeRecords,
                    },
                    documents: {
                        label: "Digitale Personalakte",
                        icon: "icon.file.collection",
                        permission: "manage.employees.documents",
                        permitted: forEmployee,
                        menuSection: employeeRecords,
                    },
                    advances: {
                        label: "Vorschüsse",
                        icon: "icon.object.labour-cost",
                        permission: "manage.employees.advances",
                        permitted: forHiredEmployee,
                        menuSection: employeeRecords,
                    },
                    ledgers: {
                        label: "Konten (Übersicht)",
                        icon: "icon.object.time-balance",
                        permitted: (access) => allLedgers(access) && forHiredEmployee(access),
                        menuSection: employeeRecords,
                    },
                    "core-data": {
                        label: "Stammdaten",
                        icon: "icon.object.master-data",
                        permission: "manage.employees.core_data",
                        permitted: forEmployee,
                        menuSection: employeeProfile,
                    },
                    "payroll-data": {
                        label: "Lohndaten",
                        icon: "icon.object.cash-count",
                        available: ({ company }) => !!company?.features.includes(Feature.PentacodePayroll),
                        permitted: (access) => access.owner && forHiredEmployee(access),
                        menuSection: employeeProfile,
                    },
                    positions: {
                        label: "Positionen",
                        icon: "icon.object.position",
                        permission: "manage.employees.positions",
                        permitted: forEmployee,
                        menuSection: employeeProfile,
                    },
                    permissions: {
                        label: "Zugänge & Rechte",
                        icon: "icon.object.permissions",
                        permission: "manage.employees.permissions",
                        permitted: forHiredEmployee,
                        menuSection: employeeProfile,
                    },
                    contracts: {
                        label: "Verträge",
                        icon: "icon.object.contract",
                        permission: "manage.employees.contract",
                        permitted: forHiredEmployee,
                        menuSection: employeeProfile,
                    },
                },
            },
        },
    },
    recruiting: { label: "Recruiting", icon: "icon.feature.recruiting", permission: "manage.recruiting" },
    revenues: {
        label: "Umsätze",
        icon: "icon.feature.revenues",
        permission: "manage.revenues",
        available: notEmbedded,
        children: {
            all: { label: "Alle Umsätze", icon: "icon.object.revenue-overview", permission: "manage.revenues.all" },
            daily: { label: "Tagesabrechnung", icon: "icon.object.daily-closing", permission: "manage.revenues.daily" },
            cashbook: { label: "Kassenbuch", icon: "icon.object.cashbook", permission: "manage.revenues.cashbook" },
            debtors: { label: "Debitoren", icon: "icon.object.invoice", permission: "manage.revenues.debtors" },
            groups: {
                label: "Umsatzgruppen",
                icon: "icon.object.revenue-group",
                permission: "manage.revenues.groups",
                linkPath: "groups/sales",
            },
            attribution: {
                label: "Erlöszuordnung",
                icon: "icon.object.revenue-allocation",
                permission: "manage.revenues.groups",
            },
        },
    },
    planning: {
        label: "Planung",
        icon: "icon.feature.planning",
        permission: "manage.planning",
        available: notEmbedded,
        children: {
            revenue: { label: "Umsätze", icon: "icon.feature.revenues", permission: "manage.planning.revenue" },
            roster: {
                label: "Stundenvorgaben",
                icon: "icon.planning.staffing-target",
                permission: "manage.planning.roster",
            },
        },
    },
    reports: {
        label: "Berichte",
        icon: "icon.feature.reports",
        permission: "manage.reports",
        available: notEmbedded,
        children: {
            overview: { label: "Übersicht", icon: "icon.object.revenue-overview" },
            savings: { label: "Ersparnis", icon: "icon.object.bank-deposit", permission: "manage.reports.savings" },
            costs: { label: "Personalkosten", icon: "icon.object.labour-cost", permission: "manage.reports.costs" },
            time: { label: "Arbeitszeit", icon: "icon.object.working-hours", permission: "manage.reports.time" },
            revenue: { label: "Umsätze", icon: "icon.object.total", permission: "manage.reports.revenue" },
        },
    },
    settings: {
        label: "Einstellungen",
        icon: "icon.feature.settings",
        children: {
            account: { label: "Benutzer", icon: "icon.object.account" },
            company: { label: "Unternehmen", icon: "icon.object.company", permission: "manage.settings.company" },
            venues: { label: "Arbeitsbereiche", icon: "icon.object.work-area", permission: "manage.settings.venues" },
            time: { label: "Zeiterfassung", icon: "icon.feature.time-tracking", permission: "manage.settings.time" },
            "core-data": {
                label: "Stammdaten",
                icon: "icon.object.master-data",
                permission: "manage.settings.core_data",
            },
            documents: { label: "Dokumente", icon: "icon.file.collection", permission: "manage.settings.documents" },
            bonuses: { label: "Zuschläge", icon: "icon.object.bonus", permission: "manage.settings.bonuses" },
            benefits: {
                label: "Sonderleistungen",
                icon: "icon.object.benefit",
                permission: "manage.settings.benefits",
            },
            accounting: {
                label: "Buchhaltung",
                icon: "icon.object.accounting",
                permission: "manage.settings.accounting",
            },
            "ai-assistant": {
                label: "Yna",
                icon: "icon.feature.ai-assistant",
                // TODO: drop the AiAssistantSettings condition when the feature is ready for production
                available: ({ dev, company, role }) =>
                    (dev || !!company?.features.includes(Feature.AiAssistantSettings)) &&
                    !!company?.features.includes(Feature.voiceControlAi) &&
                    role === Role.Owner,
            },
            billing: {
                label: "Rechnungsstellung",
                icon: "icon.object.invoice",
                permission: "manage.settings.billing",
                hideWhenInaccessible: true,
                available: ({ embedded, company }) => !embedded && !!company?.billing,
            },
        },
    },
    issues: { label: "Probleme", icon: "icon.feature.issues", permission: "manage.issues" },
    exports: {
        label: "Datenexport",
        icon: "icon.feature.exports",
        permission: "manage.exports",
        children: {
            "payroll-report-detailed": payrollExport("Lohnbericht (Detailliert)", "icon.file.pdf"),
            "payroll-report-overview": payrollExport("Lohnbericht (Übersicht)", "icon.file.pdf"),
            "payroll-datev-lg": payrollExport("DATEV Lohn & Gehalt", "icon.file.payroll"),
            "payroll-datev-lodas": payrollExport("DATEV Lodas", "icon.file.payroll"),
            "payroll-agenda": payrollExport("Agenda", "icon.file.csv"),
            "payroll-agenda-calendar": payrollExport("Agenda Kalenderdaten", "icon.file.excel"),
            "payroll-agenda-employee-core-data": payrollExport("Agenda Mitarbeiter-Stammdaten", "icon.file.excel"),
            "payroll-lexware": payrollExport("Lexware", "icon.file.csv"),
            "payroll-addison": payrollExport("Addison", "icon.file.payroll"),
            "payroll-lohn-ag": payrollExport("Lohn-AG", "icon.file.csv"),
            "payroll-sage": payrollExport("Sage", "icon.file.payroll"),
            "payroll-bmd": { ...payrollExport("BMD Lohndaten", "icon.file.csv"), available: austriaOnly },
            "payroll-bmd-absences": {
                ...payrollExport("BMD Nichtleistungszeiten", "icon.file.csv"),
                available: austriaOnly,
            },
            "time-sheet": {
                label: "Arbeitszeit-Nachweis",
                icon: "icon.file.pdf",
                permission: "manage.exports.time",
                menuSection: workingTime,
            },
            "roster-excel": {
                label: "Dienstplan",
                icon: "icon.file.excel",
                permission: "manage.exports.roster",
                menuSection: roster,
            },
            "balances-time-excel": {
                label: "Arbeitszeitkonto",
                icon: "icon.file.excel",
                permission: "manage.exports.ledgers_time",
                menuSection: balances,
            },
            "balances-vacation-excel": {
                label: "Urlaubskonto",
                icon: "icon.file.excel",
                permission: "manage.exports.ledgers_vacation",
                menuSection: balances,
            },
            "balances-bonuses-excel": {
                label: "Zuschlagskonto",
                icon: "icon.file.excel",
                permission: "manage.exports.ledgers_bonuses",
                menuSection: balances,
            },
            "balances-overtime-excel": {
                label: "Mehr- und Überstunden",
                icon: "icon.file.excel",
                permission: "manage.exports.ledgers_time",
                menuSection: balances,
                available: austriaOnly,
            },
            "balances-average-work-time-excel": {
                label: "Durchschnittliche Arbeitszeit",
                icon: "icon.file.excel",
                permission: "manage.exports.ledgers_time",
                menuSection: balances,
                available: austriaOnly,
            },
            "employee-data-pdf": {
                label: "Datenblatt",
                icon: "icon.file.pdf",
                permission: "manage.exports.employee_data",
                menuSection: masterData,
            },
            "employee-data-csv": {
                label: "CSV-Export",
                icon: "icon.file.csv",
                permission: "manage.exports.employee_data",
                menuSection: masterData,
            },
            "cashbook-pdf": {
                label: "Ausdruck / PDF",
                icon: "icon.file.pdf",
                permission: "manage.exports.cashbook",
                menuSection: cashbook,
            },
            "cashbook-datev": {
                label: "DATEV Buchungsstapel",
                icon: "icon.file.csv",
                permission: "manage.exports.cashbook",
                menuSection: cashbook,
            },
            "cashbook-datev-online": {
                label: "Datev Online",
                icon: "icon.file.csv",
                permission: "manage.exports.cashbook",
                menuSection: cashbook,
            },
            "cashbook-lexware": {
                label: "Lexware",
                icon: "icon.file.csv",
                permission: "manage.exports.cashbook",
                menuSection: cashbook,
            },
            "cashbook-generic": {
                label: "Generisch",
                icon: "icon.file.csv",
                permission: "manage.exports.cashbook",
                menuSection: cashbook,
            },
            "employee-table-excel": {
                label: "Individuelle Excel-Tabelle",
                icon: "icon.file.excel",
                permitted: ({ hasPermission }) => hasAnyRequiredPermissions(hasPermission),
                menuSection: custom,
            },
        },
    },
    feedback: {
        label: "Feedback",
        icon: "icon.action.send-feedback",
        available: notEmbedded,
        menuGroup: "footer",
    },
});

export type AppRoutePath = RoutePath<typeof appRoutes>;

export type AppPage = keyof typeof appRoutes;

export const appPages = routeKeys(appRoutes);

export function appRoute(page: AppPage): RouteNode {
    return appRoutes[page];
}

export function menuPages(group?: RouteNode["menuGroup"]): AppPage[] {
    return appPages.filter((page) => appRoute(page).menuGroup === group);
}

function isAppPage(page: string): page is AppPage {
    return Object.hasOwn(appRoutes, page);
}

export function hasPageAccess(page: string, access: RouteAccess): boolean {
    return isAppPage(page) && canAccess(appRoutes[page], access);
}

export function firstAccessiblePage(access: RouteAccess): AppPage {
    return appPages.find((page) => canAccess(appRoutes[page], access)) ?? "dashboard";
}

export type SettingsPage = keyof typeof appRoutes.settings.children;

export const settingsPages = routeKeys(appRoutes.settings.children);

export function settingsRoute(page: SettingsPage): RouteNode {
    return appRoutes.settings.children[page];
}

export type ReportsPage = keyof typeof appRoutes.reports.children;

export const reportsPages = routeKeys(appRoutes.reports.children);

export function reportsRoute(page: ReportsPage): RouteNode {
    return appRoutes.reports.children[page];
}

export type RevenuesPage = keyof typeof appRoutes.revenues.children;

export const revenuesPages = routeKeys(appRoutes.revenues.children);

export function revenuesRoute(page: RevenuesPage): RouteNode {
    return appRoutes.revenues.children[page];
}

export type PlanningPage = keyof typeof appRoutes.planning.children;

export const planningPages = routeKeys(appRoutes.planning.children);

export function planningRoute(page: PlanningPage): RouteNode {
    return appRoutes.planning.children[page];
}

export type ExportsPage = keyof typeof appRoutes.exports.children;

export const exportsPages = routeKeys(appRoutes.exports.children);

export function exportsRoute(page: ExportsPage): RouteNode {
    return appRoutes.exports.children[page];
}

export function exportsPage(format: ExportFormat): ExportsPage {
    return format.replaceAll("_", "-") as ExportsPage;
}

export function exportFormat(page: string): ExportFormat {
    return page.replaceAll("-", "_") as ExportFormat;
}

export function isExportFormat(value: string): value is ExportFormat {
    return Object.values<string>(ExportFormat).includes(value);
}

export type EmployeesAllPage = keyof typeof appRoutes.employees.children.all.children;

export const employeesAllPages = routeKeys(appRoutes.employees.children.all.children);

export function employeesAllRoute(page: EmployeesAllPage): RouteNode {
    return appRoutes.employees.children.all.children[page];
}

export type EmployeePage = keyof (typeof appRoutes.employees.children)[":employeeId"]["children"];

export const employeePages = routeKeys(appRoutes.employees.children[":employeeId"].children);

export function employeeRoute(page: EmployeePage): RouteNode {
    return appRoutes.employees.children[":employeeId"].children[page];
}

export function appPath<P extends AppRoutePath>(path: P, ...params: RouteParamArgs<P>): string {
    return routePath(path, ...params);
}
